import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import QuoteComponentsFilter from './QuoteComponentsFilter';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { Skeleton } from '@mui/material';
import Numeral from 'numeral';

import ViveScorePopup from '../../components/universal_quoter/ViveScoreCardModal';
import { hasQuoteValidActions } from './utils/quoteUtils';

// Simple tab styles
const simpleTabStyles = `
  .tab-transition {
    transition: all 0.2s ease;
  }
  
  .start-eapp-button {
    padding: 8px 16px !important;
    background-color: #333 !important;
    color: #fff !important;
    border: none !important;
    border-radius: 4px !important;
    font-size: 12px !important;
    cursor: pointer !important;
    font-weight: 600 !important;
    transition: background-color 0.2s ease !important;
    white-space: nowrap !important;
  }
    .start-eapp-button:hover {
    background-color: #555 !important;
  }
  
  .start-eapp-button:focus {
    outline: 2px solid #007bff !important;
    outline-offset: 2px;
  }
  
  .start-eapp-button:disabled {
    background-color: #ccc !important;
    color: #666 !important;
    cursor: not-allowed !important;
  }
  
  .start-eapp-button:disabled:hover {
    background-color: #ccc !important;
  }
  
  .more-details-button {
    padding: 6px 12px !important;
    background-color: transparent !important;
    border: 1px solid #dee2e6 !important;
    border-radius: 4px !important;
    font-size: 12px !important;
    color: #6c757d !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    white-space: nowrap !important;
  }
  
  .more-details-button:hover {
    border-color: #007bff !important;
    color: #007bff !important;
  }
  
  .more-details-button:focus {
    outline: 2px solid #007bff !important;
    outline-offset: 2px;
  }

  @media (max-width: 1400px) {
    .quote-actions-column {
      flex-direction: column !important;
      align-items: stretch !important;
      justify-content: center !important;
      gap: 6px !important;
    }

    .quote-actions-column .start-eapp-button,
    .quote-actions-column .more-details-button {
      width: 100% !important;
      text-align: center !important;
    }

    .quote-actions-column .compare-checkbox-inline {
      margin-right: 0 !important;
      justify-content: center !important;
    }

    .quote-carrier-column {
      flex-direction: column !important;
      align-items: flex-start !important;
    }

    .quote-carrier-column .quote-carrier-name,
    .quote-carrier-column .quote-product-name {
      text-align: left !important;
    }
  }
`;
// Inject minimal styles
if (typeof document !== 'undefined' && !document.getElementById('simple-tab-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'simple-tab-styles';
  styleElement.textContent = simpleTabStyles;
  document.head.appendChild(styleElement);
}

const QuoteResults = ({
  quotesByProductType = {},
  selectedProductTypes = [],
  loadingStates = {},
  compareSelections = {},
  onCompareSelectionsChange,
  onDetailsClick,
  onStartEApp,
  quoteType = 'annuity',
  carrierFilter = {},
  onClearFilters,
  originalQuotesData = {},
  hasQuotesBeenLoaded = false,
  hasActiveFilters = false,
  alqQuoter = null,
  aaqQuoter = null,
  formData = null
}) => {
  // Set initial active tab based on quote type and selected product types
  const getInitialActiveTab = () => {
    if (selectedProductTypes.length > 0) {
      return selectedProductTypes[0];
    }

    // Default tabs based on quote type when no product types are selected
    if (quoteType === 'life') {
      return 'Term';
    } else if (quoteType === 'annuity') {
      return 'FA';
    }

    return 'FA'; // Ultimate fallback
  };
  const [activeProductTab, setActiveProductTab] = useState(getInitialActiveTab());
  const [sortConfig, setSortConfig] = useState(() => {
    // Set default sorting for annuity quotes to accumulation value descending (highest first)
    // Set default sorting for life quotes to premium descending (highest first)
    if (quoteType === 'annuity') {
      if (formData?.solveFor === 'Income') {
        return { key: 'monthly_income', direction: 'desc' };
      } else {
        return { key: 'accumulation', direction: 'desc' };
      }
    } else if (quoteType === 'life') {
      // If solving for Face Amount, default sort by face_amount descending
      if (formData?.solveFor === 'Face' || formData?.solveFor === 'Face Amount') {
        return { key: 'face_amount', direction: 'desc' };
      }
      return { key: 'premium', direction: 'desc' };
    }
    return { key: null, direction: 'asc' };
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredButton, setHoveredButton] = useState(null);

  // Track quote data hash to detect actual changes (not just reference changes)
  const quotesDataHashRef = React.useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10); // Default to 10 items per page

  // Helper function to check if a quote has custom actions available.
  // Delegates to the shared hasQuoteValidActions utility so that QuoteResults
  // and UniversalQuoterComponent always use identical filtering logic.
  const hasCustomActionsForQuote = quote => hasQuoteValidActions(quote, { quoteType, alqQuoter, aaqQuoter });

  // Update active tab when selected product types change
  useEffect(() => {
    if (selectedProductTypes.length > 0 && !selectedProductTypes.includes(activeProductTab)) {
      setActiveProductTab(selectedProductTypes[0]);
    }
  }, [selectedProductTypes, activeProductTab]); // Reset sort configuration when quote type changes to prevent column sorting issues
  // Also update active tab if needed when quote type changes

  // Track previous values to detect actual changes (not just reference changes)
  const prevSelectedProductTypesRef = React.useRef(selectedProductTypes);
  const prevQuoteTypeRef = React.useRef(quoteType);

  useEffect(() => {
    // Check if quoteType actually changed
    const quoteTypeChanged = prevQuoteTypeRef?.current !== quoteType;

    const prevProductTypes = prevSelectedProductTypesRef?.current || [];
    const productTypesChanged = prevProductTypes?.length !== selectedProductTypes?.length || prevProductTypes?.some((type, index) => type !== selectedProductTypes[index]);

    // Only run when quote type or selected product types change (not when activeProductTab changes)
    if (quoteTypeChanged || productTypesChanged) {
      setCurrentPage(1);
      setSearchTerm(''); // Clear search when quote type changes
    }

    // Immediately update active tab based on quote type and available product types
    if (selectedProductTypes.length > 0 && !selectedProductTypes.includes(activeProductTab)) {
      setActiveProductTab(selectedProductTypes[0]);
    } else if (selectedProductTypes.length === 0) {
      // Set default tab based on quote type when no product types are selected yet
      if (quoteType === 'life') {
        setActiveProductTab('Term');
      } else if (quoteType === 'annuity') {
        setActiveProductTab('FA');
      }
    }
    // Update the ref for next comparison
    prevSelectedProductTypesRef.current = selectedProductTypes;
    prevQuoteTypeRef.current = quoteType;
  }, [quoteType, selectedProductTypes]);

  // Helper function to check if product type is Term (handles variations like "5 Year Term, 10 Year Term, ...., 40 Year Term")
  const isTermProductType = productType => {
    if (!productType) return false;
    const productTypeStr = String(productType).toUpperCase();
    return productTypeStr.includes('YEAR TERM') && !productTypeStr.includes('YEAR TERM ROP');
  };

  // Helper function to check if product type is IUL (handles variations like "To age 65 ( IUL )")
  const isIULProductType = productType => {
    if (!productType) return false;
    const productTypeStr = String(productType).toUpperCase();
    return productTypeStr === 'IUL' || productTypeStr.includes('( IUL )') || productTypeStr.includes('(IUL)');
  };

  // Separate effect to handle sort config when active tab changes
  useEffect(() => {
    // Set appropriate default sort based on quote type and product tab
    if (quoteType === 'annuity') {
      let defaultSortKey = { key: 'accumulation', direction: 'desc' };
      if (activeProductTab === 'FIA' && (formData?.solveFor === 'Contribution' || formData?.solveFor === 'GMBIncome')) {
        defaultSortKey = { key: 'contribution', direction: 'asc' };
      } else if (formData?.solveFor === 'Contribution') {
        defaultSortKey = { key: 'contribution', direction: 'asc' };
      } else {
        defaultSortKey = { key: 'accumulation', direction: 'desc' };
      }
      setSortConfig(defaultSortKey);
    } else if (quoteType === 'life') {
      // If solving for Face Amount, default sort by face_amount descending
      if (formData?.solveFor === 'Face' || formData?.solveFor === 'Face Amount') {
        setSortConfig({ key: 'face_amount', direction: 'desc' });
      }
      // For IUL, default sort by accumulation value descending
      else if (isIULProductType(activeProductTab)) {
        setSortConfig({ key: 'accumulation', direction: 'desc' });
      } else if (isTermProductType(activeProductTab) && alqQuoter?.vive_score_enabled && alqQuoter?.default_sort_hexure_score) {
        setSortConfig({ key: 'product_score', direction: 'desc' });
      } else {
        setSortConfig({ key: 'premium', direction: 'desc' });
      }
    } else {
      setSortConfig({ key: null, direction: 'asc' });
    }
  }, [quoteType, activeProductTab, formData]);

  // Track previous activeProductTab to only reset page when tab actually changes
  const prevActiveProductTabRef = React.useRef(activeProductTab);

  // Reset pagination only when actual data changes (not just reference changes)
  useEffect(() => {
    // Create a hash of the actual quote data to detect real changes
    const currentHash = JSON.stringify({
      keys: Object.keys(quotesByProductType).sort(),
      counts: Object.fromEntries(Object.entries(quotesByProductType).map(([key, quotes]) => [key, quotes?.length || 0]))
    });

    // Check if activeProductTab changed
    const tabChanged = prevActiveProductTabRef.current !== activeProductTab;

    // Check if quote data actually changed (new quote run)
    const dataChanged = quotesDataHashRef.current !== null && quotesDataHashRef.current !== currentHash;

    // Only reset page when tab changes OR when we get new quote data
    if (tabChanged) {
      setCurrentPage(1);
      prevActiveProductTabRef.current = activeProductTab;
    } else if (dataChanged) {
      setCurrentPage(1);
    }

    // Update the hash reference
    quotesDataHashRef.current = currentHash;
  }, [activeProductTab, quotesByProductType, selectedProductTypes]);

  // Get current quotes for active product tab (with carrier filtering applied)
  const getFilteredQuotes = () => {
    const baseQuotes = quotesByProductType[activeProductTab] || [];

    // Handle new carrier filter structure (object with product types as keys)
    let filteredQuotes = baseQuotes;
    if (carrierFilter && typeof carrierFilter === 'object') {
      // Check if it's the new format (object) or legacy format (array)
      const isNewFormat = !Array.isArray(carrierFilter);
      let relevantFilters = [];

      if (isNewFormat) {
        // New format: get filters for current product tab
        relevantFilters = carrierFilter[activeProductTab] || [];
      } else {
        // Legacy format: use the array directly
        relevantFilters = carrierFilter;
      }

      if (relevantFilters.length > 0) {
        filteredQuotes = baseQuotes.filter(quote => {
          const carrierName = quote.carrier_name || quote.carrier || quote.company_name || '';
          const carrierId = quote.carrier_id || quote.carrier || '';

          return relevantFilters.some(filterId => {
            // Handle both string and object formats, ensure we always have strings
            const filterValue = typeof filterId === 'object' ? filterId.id : filterId;
            const filterValueStr = String(filterValue || '');
            const carrierNameStr = String(carrierName || '');
            const carrierIdStr = String(carrierId || '');

            return filterValueStr === carrierIdStr || filterValueStr === carrierNameStr || carrierNameStr.toLowerCase().includes(filterValueStr.toLowerCase());
          });
        });
      }
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filteredQuotes = filteredQuotes.filter(quote => {
        const carrierName = (quote.carrier_name || quote.carrier || quote.company_name || '').toLowerCase();
        const productName = (quote.product_name || quote.product || quote.plan_name || '').toLowerCase();

        return carrierName.includes(searchLower) || productName.includes(searchLower);
      });
    }

    return filteredQuotes;
  };

  // Check if there are any active carrier filters
  const hasActiveCarrierFilters = () => {
    if (!carrierFilter || typeof carrierFilter !== 'object') {
      return false;
    }

    const isNewFormat = !Array.isArray(carrierFilter);
    let relevantFilters = [];

    if (isNewFormat) {
      relevantFilters = carrierFilter[activeProductTab] || [];
    } else {
      relevantFilters = carrierFilter;
    }

    return relevantFilters.length > 0;
  };

  // Check if there are original quotes available (to determine if clearing filters would help)
  const hasOriginalQuotes = () => {
    const originalQuotes = originalQuotesData[activeProductTab] || [];
    return originalQuotes.length > 0;
  };
  const currentQuotes = getFilteredQuotes();
  const isCurrentTabLoading = loadingStates[activeProductTab] || false;
  const currentCompareSelections = compareSelections[activeProductTab] || []; // Check if any tab is loading to show skeleton for entire table
  const isAnyTabLoading = Object.values(loadingStates).some(loading => loading); // Show skeleton when loading, but be more careful about when to show it
  const baseQuotes = quotesByProductType[activeProductTab] || [];
  const shouldShowSkeleton = () => {
    // If we haven't initiated any quote request yet, don't show skeleton
    if (!hasQuotesBeenLoaded) {
      return false;
    }

    // If the current active tab is specifically loading, show skeleton
    if (isCurrentTabLoading) {
      return true;
    }

    // Don't show skeleton if we have active filters or search (show "no results" message instead)
    if (hasActiveCarrierFilters() || searchTerm || hasActiveFilters) {
      return false;
    }

    return false;
  }; // Check if we have actual quote results loaded (not just empty arrays)
  const hasQuoteResults = () => {
    // If quotes haven't been loaded at least once, always show skeleton
    if (!hasQuotesBeenLoaded) {
      return false;
    }

    // If we're currently loading, show skeleton
    if (isAnyTabLoading) {
      return false;
    }

    // If quotesByProductType is empty object or has no data, return false
    if (!quotesByProductType || Object.keys(quotesByProductType).length === 0) {
      return false;
    }

    const hasData = Object.values(quotesByProductType).some(quotes => Array.isArray(quotes) && quotes.length > 0);
    return hasData;
  };

  function formatPercentage(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    const rounded = Math.round(num / 0.05) * 0.05; // snap to nearest 0.05
    return `${rounded.toFixed(2)}%`;
  }

  // Compare functionality for current tab
  const handleCompareToggle = quoteId => {
    const currentSelections = compareSelections[activeProductTab] || [];
    let newSelections;

    if (currentSelections.includes(quoteId)) {
      newSelections = currentSelections.filter(id => id !== quoteId);
    } else {
      newSelections = [...currentSelections, quoteId];
    }

    onCompareSelectionsChange({
      ...compareSelections,
      [activeProductTab]: newSelections
    });
  };

  // Get total count of selected items across all tabs for compare
  const getTotalCompareCount = () => {
    return Object.values(compareSelections).reduce((total, selections) => total + selections.length, 0);
  };

  // Reset to first page when search term or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const getPremiumValueByMode = (quote, premiumMode) => {
    switch (premiumMode) {
      case 'Annual':
        return quote?.premium_annual || 0;
      case 'Semi-Annual':
        return quote?.premium_semi_annual || 0;
      case 'Quarterly':
        return quote?.premium_quarterly || 0;
      case 'Monthly':
        return quote?.premium_monthly || 0;
      default:
        return 0;
    }
  };

  // Calculate pagination
  const isShowAll = itemsPerPage === 'all';
  const effectiveItemsPerPage = isShowAll ? currentQuotes.length : itemsPerPage;
  const totalPages = isShowAll ? 1 : Math.max(1, Math.ceil(currentQuotes.length / effectiveItemsPerPage));
  const startIndex = isShowAll ? 0 : (currentPage - 1) * effectiveItemsPerPage;
  const endIndex = isShowAll ? currentQuotes.length : startIndex + effectiveItemsPerPage; // Sort quotes based on current sort configuration, then apply pagination
  const sortedQuotes = React.useMemo(() => {
    let sorted = currentQuotes;

    if (sortConfig.key) {
      sorted = [...currentQuotes].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle carrier column specially (sort by carrier name)
        if (sortConfig.key === 'carrier') {
          aValue = a.carrier_name || a.carrier || a.company_name || '';
          bValue = b.carrier_name || b.carrier || b.company_name || '';
        }
        if (sortConfig.key === 'contribution') {
          aValue = a.premium || a.contribution || 0;
          bValue = b.premium || b.contribution || 0;
        }
        // Handle life insurance specific fields with multiple possible field names
        if (sortConfig.key === 'premium') {
          aValue = getPremiumValueByMode(a, formData?.premiumMode);
          bValue = getPremiumValueByMode(b, formData?.premiumMode);
        }

        if (sortConfig.key === 'face_amount') {
          aValue = a.face_amount || a.faceAmount || 0;
          bValue = b.face_amount || b.faceAmount || 0;
        }

        // Handle vive score sorting
        if (sortConfig.key === 'product_score') {
          aValue = a?.vive_score_json?.Score || 0;
          bValue = b?.vive_score_json?.Score || 0;
        }

        // Handle IUL-specific fields
        if (sortConfig.key === 'accumulation') {
          aValue = a.accumulation || a.accumulationValue || a.accumulation_value || 0;
          bValue = b.accumulation || b.accumulationValue || b.accumulation_value || 0;
        }

        if (sortConfig.key === 'premium_load') {
          aValue = a.premium_load || a.premiumLoad || a.current_premium_load || 0;
          bValue = b.premium_load || b.premiumLoad || b.current_premium_load || 0;
        }

        // Handle undefined/null values - "has value" sorts above "no value"
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1; // null/undefined goes to end (no value)
        if (bValue == null) return -1; // has value goes to beginning

        // Handle numeric values (remove $ and commas)
        if (typeof aValue === 'string' && aValue.includes('$')) {
          aValue = parseFloat(aValue.replace(/[$,]/g, '')) || 0;
          bValue = parseFloat((bValue || '').replace(/[$,]/g, '')) || 0;
        }

        // Handle percentage values
        if (typeof aValue === 'string' && aValue.includes('%')) {
          aValue = parseFloat(aValue.replace('%', '')) || 0;
          bValue = parseFloat((bValue || '').replace('%', '')) || 0;
        }

        // Handle numeric values directly
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }

        // Handle string values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        return 0;
      });
    }

    // Apply pagination after sorting
    return sorted.slice(startIndex, endIndex);
  }, [currentQuotes, sortConfig, startIndex, endIndex]);
  const handleSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key !== key) {
      // For life insurance quotes, start with descending for numeric columns
      if (quoteType === 'life' && ['premium', 'face_amount', 'product_score'].includes(key)) {
        direction = 'desc';
      }
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = columnKey => {
    if (sortConfig.key !== columnKey) {
      return <FaSort style={{ fontSize: 14, verticalAlign: 'middle' }} />;
    }
    return sortConfig.direction === 'asc' ? <FaSortUp style={{ fontSize: 14, verticalAlign: 'middle' }} /> : <FaSortDown style={{ fontSize: 14, verticalAlign: 'middle' }} />;
  };

  const styles = {
    container: {
      backgroundColor: '#f8f9fa',
      minHeight: '500px',
      padding: '20px'
    },
    table: {
      width: '100%',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      border: '1px solid #e9ecef'
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e9ecef',
      fontWeight: '600',
      fontSize: '13px',
      color: '#495057'
    },
    headerCell: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      whiteSpace: 'nowrap',
      lineHeight: '1.2'
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      borderBottom: '1px solid #f8f9fa',
      transition: 'background-color 0.2s ease'
    },
    rowHover: {
      backgroundColor: '#f8f9fa'
    },
    carrierColumn: {
      flex: '1',
      minWidth: '125px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    carrierNameColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    carrierName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#333',
      lineHeight: '1.3'
    },
    productName: {
      fontSize: '12px',
      color: '#6c757d',
      lineHeight: '1.3'
    },
    companyLogo: {
      width: '126px',
      maxWidth: '140px',
      height: '32px',
      objectFit: 'contain',
      borderRadius: '4px',
      flexShrink: 0
    },
    dataColumn: {
      flex: '0 0 140px',
      textAlign: 'center',
      fontSize: '14px',
      fontWeight: '500',
      color: '#333'
    },
    rateColumn: {
      flex: '0 0 140px',
      textAlign: 'center',
      fontSize: '14px',
      fontWeight: '600',
      color: '#28a745'
    },
    actionColumn: {
      flex: '0 1 280px',
      minWidth: '140px',
      textAlign: 'center',
      display: 'flex',
      gap: '8px',
      justifyContent: 'center',
      alignItems: 'center'
    },
    compareCheckbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      color: '#6c757d',
      marginRight: '8px'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '20px',
      padding: '16px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e9ecef'
    },
    pageSelect: {
      padding: '8px 12px',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      backgroundColor: '#fff',
      fontSize: '14px'
    },
    pageButton: {
      padding: '8px 16px',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'background-color 0.2s'
    },
    // Simple tab styles
    tabContainer: {
      display: 'flex',
      marginBottom: '20px',
      backgroundColor: '#f8f9fa',
      padding: '4px',
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      gap: '4px',
      flexWrap: 'wrap' // allows tabs to wrap on smaller screens
    },
    tab: {
      padding: '12px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
      backgroundColor: 'transparent',
      color: '#6c757d',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: '120px',
      justifyContent: 'center',
      flex: '0 1 auto' // shrinks to fit, doesn't force grow
    },
    activeTab: {
      backgroundColor: '#007bff',
      color: '#ffffff'
    },
    inactiveTab: {
      backgroundColor: '#ffffff',
      color: '#6c757d',
      border: '1px solid #e9ecef'
    },
    tabBadge: {
      backgroundColor: '#ffffff',
      color: '#007bff',
      borderRadius: '12px',
      padding: '2px 6px',
      fontSize: '11px',
      fontWeight: '600',
      minWidth: '18px',
      textAlign: 'center'
    },
    inactiveTabBadge: {
      backgroundColor: '#6c757d',
      color: '#ffffff'
    },
    emptyTabBadge: {
      backgroundColor: '#dc3545',
      color: '#ffffff'
    },
    filterBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e9ecef'
    },
    filterSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    filterButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease'
    },
    filterButtonActive: {
      backgroundColor: '#0056b3'
    },
    activeFilters: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    filterTag: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500'
    },
    filterTagClose: {
      background: 'none',
      border: 'none',
      color: '#1976d2',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    clearAllFilters: {
      background: 'none',
      border: '1px solid #dc3545',
      color: '#dc3545',
      padding: '6px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    resultsCount: {
      fontSize: '14px',
      color: '#6c757d',
      fontWeight: '500'
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap', // allow wrapping on smaller screens
      gap: '10px',
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e9ecef'
    },
    searchLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#495057',
      whiteSpace: 'normal', // was: 'nowrap'
      overflowWrap: 'anywhere',
      lineHeight: '1.3'
    },
    searchFieldGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: '1 1 320px', // grows, shrinks, wraps
      minWidth: '220px'
    },
    searchInput: {
      flex: '1 1 auto',
      minWidth: 0,
      padding: '10px 16px',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    searchInputFocused: {
      borderColor: '#007bff',
      boxShadow: '0 0 0 2px rgba(0, 123, 255, 0.25)'
    },
    clearSearchButton: {
      flexShrink: 0,
      padding: '8px 12px',
      backgroundColor: '#6c757d',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease'
    }
  };
  // Generate product type display names dynamically based on available data
  const getProductTypeDisplayName = productType => {
    // First, check if we have quotes for this product type to extract display name from API response
    const quotes = quotesByProductType[productType] || [];
    if (quotes.length > 0) {
      // Look for display name in the first quote
      const firstQuote = quotes[0];
      const displayName = firstQuote.product_type_display_name || firstQuote.productTypeDisplayName || firstQuote.product_type_name || firstQuote.productTypeName;

      if (displayName) {
        return displayName;
      }
    }

    // Fallback: Generate a readable name from the product type key
    // Handle common abbreviations and formats
    const fallbackNames = {
      // Common annuity types
      FA: 'Fixed Annuity',
      FIA: 'Fixed Indexed Annuity',
      MYGA: 'Multi-Year Guaranteed Annuity',

      // Common life insurance types
      Term: 'Term Life',
      GUL: 'Guaranteed Universal Life',
      ROP: 'Return of Premium',
      Other: 'Other Products'
    };

    // Check if we have a predefined fallback
    if (fallbackNames[productType]) {
      return fallbackNames[productType];
    }

    // For complex product types like "10 Year Term", "20 Year ROP", etc.
    // Just return them as-is since they're already descriptive
    if (productType.includes('Year') || productType.includes('Term') || productType.includes('ROP')) {
      return productType;
    }

    // For unknown product types, convert camelCase/snake_case to Title Case
    return productType
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }; // Define columns based on quote type and product type
  const columns = React.useMemo(() => {
    // For life insurance, determine columns based on activeProductTab
    if (quoteType === 'life') {
      const baseColumns = [
        { key: 'carrier', label: 'Carrier/Product', sortable: true },
        { key: 'health_class', label: 'Health Class', sortable: false },
        { key: 'face_amount', label: 'Face Amount', sortable: true }
      ];

      // IUL-specific columns (handle variations like "To age 65 ( IUL )")
      if (isIULProductType(activeProductTab)) {
        baseColumns.push({ key: 'accumulation', label: 'Accumulation Value', sortable: true }, { key: 'premium_load', label: 'Current Premium Load', sortable: true });
      }

      // Standard life columns for non-IUL products
      baseColumns.push({ key: 'premium', label: `${formData?.premiumMode} Premium`, sortable: true });

      // Term specific column & only for solveFor Premium & vive score display if enabled on poseidon
      if (isTermProductType(activeProductTab) && formData?.solveFor === 'Premium' && alqQuoter?.vive_score_enabled) {
        baseColumns.push({ key: 'product_score', label: 'Product Score', sortable: true });
      }

      baseColumns.push({ key: 'actions', label: 'Actions', sortable: false });

      return baseColumns;
    }

    // For annuity quotes, use product tab to determine columns (but only if we're actually in annuity mode)
    if (quoteType === 'annuity') {
      if (activeProductTab === 'FIA') {
        const baseColumns = [
          { key: 'carrier', label: 'Carrier/Product', sortable: false },
          { key: 'surrender_charge', label: 'Surrender Charge', sortable: true },
          { key: 'oneYearPTPRate', label: '1 YR PTP Rate', sortable: true }
        ];
        if (formData?.solveFor === 'Income') {
          baseColumns.push({ key: 'monthly_income', label: 'Monthly Income', sortable: true });
        } else {
          baseColumns.push({ key: 'accumulation', label: 'Accumulation Value', sortable: true });
        }

        if (formData?.solveFor === 'Contribution' || formData?.solveFor === 'GMBIncome') {
          baseColumns.push({ key: 'contribution', label: 'Required Contribution', sortable: true });
        }
        baseColumns.push({ key: 'actions', label: 'Actions', sortable: false });
        return baseColumns;
      } else {
        // FA or MYGA
        const baseColumns = [
          { key: 'carrier', label: 'Carrier/Product', sortable: false },
          { key: 'surrender_charge', label: 'Surrender Charge', sortable: true },
          { key: 'gtd_period', label: 'Guaranteed Period', sortable: true },
          { key: 'current_rate', label: 'Current Rate', sortable: true, render: quote => formatPercentage(quote.current_rate ?? quote.currentRate) },
          { key: 'accumulation', label: 'Accumulation Value', sortable: true }
        ];
        if (formData?.solveFor === 'Contribution') {
          baseColumns.push({ key: 'contribution', label: 'Contribution Value', sortable: true });
        }
        baseColumns.push({ key: 'actions', label: 'Actions', sortable: false });
        return baseColumns;
      }
    }

    // For LTC or any other quote type, show basic columns
    return [
      { key: 'carrier', label: 'Carrier/Product', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ];
  }, [quoteType, activeProductTab]);

  const halfShrinkColumnKeys = new Set(['product_score', 'premium', 'surrender_charge', 'gtd_period', 'current_rate', 'oneYearPTPRate']);
  const quarterShrinkColumnKeys = new Set(['accumulation', 'contribution', 'monthly_income', 'face_amount', 'health_class', 'premium_load']);

  const getColumnLayoutStyle = (columnKey, includeHeaderAlignment = false) => {
    if (columnKey === 'carrier' || columnKey.includes('carrier')) {
      return { flex: '1', minWidth: '140px' };
    }

    if (columnKey === 'actions') {
      return includeHeaderAlignment ? { flex: '0 1 280px', minWidth: '140px', justifyContent: 'center' } : { flex: '0 1 280px', minWidth: '140px' };
    }

    if (halfShrinkColumnKeys.has(columnKey)) {
      return includeHeaderAlignment ? { flex: '0 1 140px', minWidth: '70px', textAlign: 'center', justifyContent: 'center' } : { flex: '0 1 140px', minWidth: '70px', textAlign: 'center' };
    }

    if (quarterShrinkColumnKeys.has(columnKey)) {
      return includeHeaderAlignment ? { flex: '0 1 140px', minWidth: '105px', textAlign: 'center', justifyContent: 'center' } : { flex: '0 1 140px', minWidth: '105px', textAlign: 'center' };
    }

    return includeHeaderAlignment ? { flex: '0 0 140px', textAlign: 'center', justifyContent: 'center' } : { flex: '0 0 140px', textAlign: 'center' };
  };

  const renderHeaderCell = column => {
    // Show skeleton loading in header during loading state
    if (shouldShowSkeleton()) {
      return (
        <div style={styles.headerCell}>
          <Skeleton variant='text' width={80} height={16} style={{ display: 'inline-block' }} />
        </div>
      );
    }

    const isShrinkColumn = column.key !== 'carrier' && column.key !== 'actions';
    const shrinkHeaderStyle = isShrinkColumn ? { whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' } : {};

    // If column is not sortable, just show the label
    if (!column.sortable) {
      return <div style={{ ...styles.headerCell, ...shrinkHeaderStyle }}>{column.label}</div>;
    }

    return (
      <div style={{ ...styles.headerCell, ...shrinkHeaderStyle }} onClick={() => handleSort(column.key)}>
        {column.label} <span>{getSortIcon(column.key)}</span>
      </div>
    );
  };

  const renderDataCell = (quote, column, index) => {
    if (typeof column.render === 'function') {
      const content = column.render(quote, column);
      return <div style={column.key === 'current_rate' ? styles.rateColumn : styles.dataColumn}>{content}</div>;
    }

    switch (column.key) {
      case 'carrier':
        const carrierLogoUrl = quote.carrier_logo_url || quote.carrierLogoUrl || quote.logo_url;
        const carrierName = quote.carrier_name || quote.carrier || quote.company_name || 'Unknown Carrier';
        const productName = quote.product_name || quote.product || quote.plan_name || 'Unknown Product';
        return (
          <div className='quote-carrier-column' style={styles.carrierColumn} data-itemid={`quote-row-${index}-cell-main-${column.key}`}>
            {carrierLogoUrl && (
              <img
                src={carrierLogoUrl}
                alt={`${carrierName} logo`}
                style={styles.companyLogo}
                onError={e => {
                  // Hide image if it fails to load
                  e.target.style.display = 'none';
                }}
                data-itemid={`quote-row-${index}-cell-logo-${column.key}`}
              />
            )}
            <div style={styles.carrierNameColumn} data-itemid={`quote-row-${index}-cell-wrapper-${column.key}`}>
              <div className='quote-carrier-name' style={styles.carrierName} data-itemid={`quote-row-${index}-cell-carrier-${column.key}`}>
                {carrierName}
              </div>
              <div className='quote-product-name' style={styles.productName} data-itemid={`quote-row-${index}-cell-product-${column.key}`}>
                {productName}
              </div>
            </div>
          </div>
        );
      case 'health_class':
        const healthClass = quote?.ixn_health_category || 'Unknown Health Class';
        const tobacco = typeof quote?.tobacco === 'boolean' ? (quote?.tobacco ? 'Tobacco' : 'Non-Tobacco') : 'Unknown Tobacco';
        return (
          <div style={styles.carrierNameColumn} data-itemid={`quote-row-${index}-cell-wrapper-${column.key}`}>
            <div style={styles.carrierName} data-itemid={`quote-row-${index}-cell-carrier-${column.key}`}>
              {healthClass}
            </div>
            <div style={styles.productName} data-itemid={`quote-row-${index}-cell-product-${column.key}`}>
              {tobacco}
            </div>
          </div>
        );
      case 'contribution':
        const contribution = quote.premium || quote.contribution || 0;
        const formattedContribution = typeof contribution === 'number' ? `$${contribution.toLocaleString()}` : contribution;
        return <div style={styles.dataColumn}>{formattedContribution}</div>;
      case 'surrender_charge':
      case 'surrenderCharge':
        // Handle surrender_charge which is a number and add year if gtd_period exists
        const surrenderCharge = quote.surrender_charge || quote.surrenderCharge || 0;
        const gtdPeriod = quote.gtd_period;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {surrenderCharge} {surrenderCharge !== 0 ? 'yr' : ''}
          </div>
        );
      case 'gtd_period':
        const guaranteedPeriod = quote.gtd_period || 0;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {guaranteedPeriod} {guaranteedPeriod !== 0 ? 'yr' : ''}
          </div>
        );
      case 'current_rate':
      case 'currentRate':
        const currentRate = quote.current_rate || quote.currentRate || 0;
        return (
          <div style={styles.rateColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {currentRate}%
          </div>
        );
      case 'oneYearPTPRate':
        const oneYearRate = quote.cap_rate || quote.capRate || quote.oneYearPTPRate || quote.one_year_ptp_rate || 0;
        return (
          <div style={styles.rateColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {formatPercentage(oneYearRate)}
          </div>
        );
      case 'accumulation':
      case 'accumulationValue':
        const accumulation = quote.accumulation || quote.accumulationValue || 0;
        const formattedAccumulation = typeof accumulation === 'number' ? `$${accumulation.toLocaleString()}` : accumulation;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {formattedAccumulation}
          </div>
        ); // Life insurance specific fields
      case 'monthly_income':
        const monthlyIncome = quote.monthly_income || quote.monthlyIncome || 0;
        const formattedIncome = typeof monthlyIncome === 'number' ? `$${monthlyIncome.toLocaleString()}` : monthlyIncome;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {formattedIncome}
          </div>
        );
      case 'face_amount':
        const faceAmount = quote.face_amount || quote.faceAmount || 0;
        const formattedFaceAmount = typeof faceAmount === 'number' ? `$${faceAmount.toLocaleString()}` : faceAmount;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {formattedFaceAmount}
          </div>
        );
      case 'premium':
        const premium = getPremiumValueByMode(quote, formData?.premiumMode);
        const formattedPremium = typeof premium === 'number' ? `${Numeral(premium).format('$0,0.00')}` : premium;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {formattedPremium}
          </div>
        );
      // vive score column
      case 'product_score':
        const viveScore = quote?.vive_score_json?.Score || 0;
        const formattedViveScore = typeof viveScore === 'number' ? `${viveScore.toLocaleString()}` : 0;
        return (
          <div style={{ ...styles.dataColumn, ...styles.viveScoreColumn, cursor: viveScore > 0 ? 'pointer' : 'default' }} data-itemid={`quote-row-${index}-cell-wrapper-${column.key}`}>
            {viveScore > 0 ? (
              <ViveScorePopup scoreDetails={quote?.vive_score_json?.Features} children={<span data-itemid={`quote-row-${index}-cell-${column.key}`}>{formattedViveScore}</span>} />
            ) : (
              <span data-itemid={`quote-row-${index}-cell-${column.key}`}>{formattedViveScore}</span>
            )}
          </div>
        );

      // IUL-specific fields
      case 'premium_load':
        const premiumLoad = quote.premium_load || quote.premiumLoad || quote.current_premium_load || 0;
        // Format as percentage if numeric, otherwise display as-is
        const formattedPremiumLoad = typeof premiumLoad === 'number' ? `${premiumLoad.toFixed(2)}%` : typeof premiumLoad === 'string' && premiumLoad.includes('%') ? premiumLoad : `${premiumLoad}%`;
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            {formattedPremiumLoad}
          </div>
        );

      case 'actions':
        return (
          <div className='quote-actions-column' style={styles.actionColumn} data-itemid={`quote-row-${index}-cell-${column.key}`}>
            <label className='compare-checkbox-inline' style={styles.compareCheckbox} data-itemid={`quote-row-${index}-compare-checkbox-label`}>
              <input
                data-itemid={`quote-row-${index}-compare-checkbox-input`}
                type='checkbox'
                checked={currentCompareSelections.includes(quote.id)}
                onChange={() => handleCompareToggle(quote.id)}
                style={{ marginRight: '4px' }}
              />
              Compare
            </label>{' '}
            {(() => {
              const hasActions = hasCustomActionsForQuote(quote);
              return (
                <button
                  onClick={hasActions ? () => onStartEApp(quote) : undefined}
                  className='start-eapp-button'
                  disabled={!hasActions}
                  title={!hasActions ? 'This product is not currently available on firelight' : ''}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: !hasActions ? '#ccc' : hoveredButton === `eapp-${quote.id}` ? '#555' : '#333',
                    color: !hasActions ? '#666' : '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: !hasActions ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'background-color 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={() => hasActions && setHoveredButton(`eapp-${quote.id}`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  data-itemid={`quote-row-${index}-start-eapp-button`}
                >
                  Start eApp
                </button>
              );
            })()}
            <button
              onClick={() => onDetailsClick(quote, activeProductTab)}
              className='more-details-button'
              style={{
                padding: '6px 12px',
                backgroundColor: 'transparent',
                border: hoveredButton === `details-${quote.id}` ? '1px solid #007bff' : '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '12px',
                color: hoveredButton === `details-${quote.id}` ? '#007bff' : '#6c757d',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={() => setHoveredButton(`details-${quote.id}`)}
              onMouseLeave={() => setHoveredButton(null)}
              data-itemid={`quote-row-${index}-details-button`}
            >
              More Details
            </button>
          </div>
        );
      default:
        return (
          <div style={styles.dataColumn} data-itemid={`quote-row-${index}-cell-${column?.key || 'default'}`}>
            -
          </div>
        );
    }
  };

  return (
    <div style={styles.container}>
      {/* Product Type Tabs - Always show all selected product types */}{' '}
      {selectedProductTypes.length > 0 && (
        <div style={styles.tabContainer}>
          {selectedProductTypes.map(productType => {
            const isActive = activeProductTab === productType;
            const isLoading = loadingStates[productType];
            const quoteCount = quotesByProductType[productType]?.length || 0;
            const isEmpty = !isLoading && quoteCount === 0;

            return (
              <button
                key={productType}
                style={{
                  ...styles.tab,
                  ...(isActive ? styles.activeTab : styles.inactiveTab)
                }}
                onClick={() => {
                  setActiveProductTab(productType);
                  setCurrentPage(1); // Reset pagination when switching tabs
                }}
                className='tab-transition'
                data-itemid={`tab-button-${productType}`}
              >
                <span data-itemid={`tab-button-label-${productType}`}>{getProductTypeDisplayName(productType)}</span>
                {/* Simple badge */}
                {isLoading ? (
                  <span style={{ ...styles.tabBadge, padding: '2px 8px' }} data-itemid={`tab-loading-${productType}`}>
                    <Skeleton variant='text' width={20} height={12} style={{ display: 'inline-block' }} />
                  </span>
                ) : (
                  <span
                    style={{
                      ...styles.tabBadge,
                      ...(isActive ? {} : isEmpty ? styles.emptyTabBadge : styles.inactiveTabBadge)
                    }}
                    data-itemid={`tab-badge-${productType}`}
                  >
                    {quoteCount}
                  </span>
                )}{' '}
              </button>
            );
          })}
        </div>
      )}
      {/* Search Bar */}
      <div style={styles.searchContainer} data-itemid='search-container'>
        <label style={styles.searchLabel} data-itemid='search-label'>
          Search Carrier/Product:
        </label>{' '}
        <input
          type='text'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder='Type to search carrier or product names...'
          style={{
            ...styles.searchInput,
            ...(searchTerm ? { borderColor: '#007bff' } : {})
          }}
          onFocus={e => {
            e.target.style.borderColor = '#007bff';
            e.target.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.25)';
          }}
          onBlur={e => {
            if (!searchTerm) {
              e.target.style.borderColor = '#dee2e6';
              e.target.style.boxShadow = 'none';
            }
          }}
          data-itemid='search-input'
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            style={styles.clearSearchButton}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#5a6268';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = '#6c757d';
            }}
            data-itemid='clear-search-button'
          >
            Clear
          </button>
        )}
      </div>
      {/* Quote Components Filter - only show for life quotes */}
      {/* Temporarily disabled to fix display issues */}
      {false && quoteType === 'life' && (
        <QuoteComponentsFilter
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(!isFilterOpen)}
          filterData={filterData}
          onFilterChange={setFilterData}
          quotedBy='face_amount' // This could be dynamic based on quote data
        />
      )}
      {/* Active Filters Indicator */}
      {(hasActiveCarrierFilters() || searchTerm) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#fff3cd',
            borderRadius: '6px',
            border: '1px solid #ffeaa7',
            fontSize: '14px',
            color: '#856404'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {hasActiveCarrierFilters() && <span style={{ fontWeight: '500' }}>🔍 Carrier filters are active</span>}
            {searchTerm && <span style={{ fontWeight: '500' }}>🔎 Search: "{searchTerm}"</span>}
            <span style={{ fontSize: '13px', color: '#6c757d' }}>
              ({currentQuotes.length} of {originalQuotesData[activeProductTab]?.length || quotesByProductType[activeProductTab]?.length || 0} quotes shown)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  background: 'none',
                  border: '1px solid #856404',
                  color: '#856404',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#856404';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#856404';
                }}
              >
                Clear Search
              </button>
            )}
            {hasActiveCarrierFilters() && onClearFilters && (
              <button
                onClick={onClearFilters}
                style={{
                  background: 'none',
                  border: '1px solid #856404',
                  color: '#856404',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#c82333';
                  e.target.style.borderColor = '#bd2130';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.borderColor = '#dc3545';
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}{' '}
      {/* Main table rendering logic - show table structure with skeleton headers during loading */}
      {currentQuotes.length === 0 && !shouldShowSkeleton() ? (
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            border: '1px solid #e9ecef'
          }}
          data-itemid='no-quotes-container'
        >
          <h4 style={{ margin: '0 0 12px 0', color: '#6c757d' }} data-itemid='no-quotes-title'>
            No {getProductTypeDisplayName(activeProductTab)} Quotes Found
          </h4>{' '}
          <p style={{ margin: '0 0 16px 0', color: '#6c757d', fontSize: '14px' }} data-itemid='no-quotes-message'>
            {hasActiveCarrierFilters() && hasOriginalQuotes()
              ? 'No quotes match the current filter criteria.'
              : searchTerm
                ? `No quotes found matching "${searchTerm}".`
                : 'No quotes are available for the selected criteria.'}
          </p>
          {(hasActiveCarrierFilters() && hasOriginalQuotes() && onClearFilters) || searchTerm ? (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }} data-itemid='no-quotes-actions'>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    background: '#007bff',
                    border: '1px solid #007bff',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = '#0056b3';
                    e.target.style.borderColor = '#0056b3';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = '#007bff';
                    e.target.style.borderColor = '#007bff';
                  }}
                >
                  Clear Search
                </button>
              )}
              {hasActiveCarrierFilters() && hasOriginalQuotes() && onClearFilters && (
                <button
                  onClick={onClearFilters}
                  style={{
                    background: '#dc3545',
                    border: '1px solid #dc3545',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = '#c82333';
                    e.target.style.borderColor = '#bd2130';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = '#dc3545';
                    e.target.style.borderColor = '#dc3545';
                  }}
                  data-itemid='no-quotes-clear-filters-button'
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {/* Table with headers and data - shown during loading and when we have results */}
          <div style={styles.table} data-itemid='quotes-table'>
            {/* Header Row - always shown, with skeleton during loading */}
            <div style={styles.headerRow} data-itemid='quotes-table-header'>
              {columns.map(column => (
                <div
                  key={column.key}
                  style={{
                    ...styles.headerCell,
                    ...getColumnLayoutStyle(column.key, true)
                  }}
                  data-itemid={`header-cell-${column.key}`}
                >
                  {renderHeaderCell(column)}
                </div>
              ))}
            </div>{' '}
            {/* Data Rows - skeleton during loading, actual data when loaded */}
            {shouldShowSkeleton()
              ? // Show skeleton rows during loading
                Array.from({ length: 8 }, (_, index) => (
                  <div
                    key={`skeleton-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      borderBottom: index < 7 ? '1px solid #f8f9fa' : 'none'
                    }}
                    data-itemid={`skeleton-row-${index}`}
                  >
                    {/* Skeleton row with same column structure as headers */}
                    <div style={{ flex: '1', minWidth: '125px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Skeleton variant='rectangular' width={32} height={32} style={{ borderRadius: '4px', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <Skeleton variant='text' width='80%' height={20} style={{ marginBottom: '4px' }} />
                          <Skeleton variant='text' width='60%' height={16} />
                        </div>
                      </div>
                    </div>
                    {/* Skeleton data columns matching the header structure */}
                    {columns.slice(1, -1).map((_, colIndex) => (
                      <div key={colIndex} style={{ flex: '0 0 140px', textAlign: 'center' }}>
                        <Skeleton variant='text' width='70%' height={20} style={{ margin: '0 auto' }} />
                      </div>
                    ))}
                    {/* Skeleton action buttons */}
                    <div style={{ flex: '0 1 280px', minWidth: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                        <Skeleton variant='rectangular' width={60} height={24} style={{ borderRadius: '4px' }} />
                        <Skeleton variant='rectangular' width={80} height={32} style={{ borderRadius: '4px' }} />
                        <Skeleton variant='rectangular' width={90} height={32} style={{ borderRadius: '4px' }} />
                      </div>
                    </div>
                  </div>
                ))
              : // Show actual data rows when loaded
                sortedQuotes.map((quote, index) => (
                  <div
                    key={quote.id || index}
                    style={styles.row}
                    onMouseEnter={e => (e.target.style.backgroundColor = styles.rowHover.backgroundColor)}
                    onMouseLeave={e => (e.target.style.backgroundColor = 'transparent')}
                    data-itemid={`quote-row-${index}`}
                  >
                    {columns.map(column => (
                      <div
                        key={column.key}
                        style={{
                          ...getColumnLayoutStyle(column.key)
                        }}
                        data-itemid={`quote-row-${index}-cell-top-main-${column.key}`}
                      >
                        {renderDataCell(quote, column, index)}
                      </div>
                    ))}
                  </div>
                ))}{' '}
          </div>{' '}
          {/* Pagination - only show when not loading */}
          {!shouldShowSkeleton() && (
            <div style={styles.pagination} data-itemid='pagination-container'>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }} data-itemid='pagination-info'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', color: '#6c757d', whiteSpace: 'nowrap' }} data-itemid='items-per-page-label'>
                    Show:
                  </label>
                  <select value={itemsPerPage} onChange={e => setItemsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={styles.pageSelect} data-itemid='items-per-page-select'>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value='all'>Show all</option>
                  </select>
                </div>

                <span style={{ fontSize: '14px', color: '#6c757d' }} data-itemid='pagination-info-text'>
                  {isShowAll ? `Showing all ${currentQuotes.length} quotes` : `Showing ${startIndex + 1}-${Math.min(endIndex, currentQuotes.length)} of ${currentQuotes.length} quotes`}
                </span>

                {!isShowAll && (
                  <>
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      style={{
                        ...styles.pageButton,
                        backgroundColor: currentPage === 1 ? '#6c757d' : '#007bff',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                      }}
                      data-itemid='pagination-previous-button'
                    >
                      ← Previous
                    </button>

                    <span style={{ fontSize: '14px', color: '#333' }} data-itemid='pagination-current-page'>
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        ...styles.pageButton,
                        backgroundColor: currentPage === totalPages ? '#6c757d' : '#007bff',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                      }}
                      data-itemid='pagination-next-button'
                    >
                      Next →
                    </button>
                  </>
                )}
              </div>

              {!isShowAll && (
                <select value={currentPage} onChange={e => setCurrentPage(Number(e.target.value))} style={styles.pageSelect} data-itemid='pagination-page-select'>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <option key={page} value={page} data-itemid={`pagination-page-${page}`}>
                      Page {page}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

QuoteResults.propTypes = {
  quotesByProductType: PropTypes.object,
  selectedProductTypes: PropTypes.arrayOf(PropTypes.string),
  loadingStates: PropTypes.object,
  compareSelections: PropTypes.object,
  onCompareSelectionsChange: PropTypes.func,
  onDetailsClick: PropTypes.func.isRequired,
  onStartEApp: PropTypes.func.isRequired,
  quoteType: PropTypes.oneOf(['annuity', 'life', 'ltc']),
  carrierFilter: PropTypes.object,
  onClearFilters: PropTypes.func,
  originalQuotesData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  hasQuotesBeenLoaded: PropTypes.bool,
  hasActiveFilters: PropTypes.bool,
  alqQuoter: PropTypes.object,
  aaqQuoter: PropTypes.object,
  formData: PropTypes.object
};

export default QuoteResults;
